import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'
import type { StarrableEntityType } from '@/lib/types/starred'
import useLoginStore from '@/store/LoginStore'

/**
 * Starred item document interface
 */
export interface StarredDocument extends BaseDocument {
  userId: string
  entityType: StarrableEntityType
  entityId: string
  sortOrder: string | null
}

/**
 * Starred item hooks
 */
const hooks = createEntityHooks<StarredDocument>('starred')

/**
 * Get all starred items for the current user, optionally filtered by entity type.
 */
export const useStarredItems = (entityType?: StarrableEntityType) => {
  const userId = useLoginStore((s) => s.userId)
  const filter: Partial<StarredDocument> = useMemo(() => {
    const f: Partial<StarredDocument> = { userId: userId ?? undefined }
    if (entityType) f.entityType = entityType
    return f
  }, [userId, entityType])

  const { data, isLoading, db } = hooks.useEntities(filter)

  const sorted = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) => {
        if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
        return a.createdOn - b.createdOn
      }),
    [data],
  )

  return { starredItems: sorted, isLoading, db }
}

/**
 * Check if a specific entity is starred by the current user.
 */
export const useIsStarred = (entityType: StarrableEntityType, entityId: string | undefined) => {
  const { starredItems, isLoading } = useStarredItems(entityType)

  const starredItem = useMemo(
    () => (entityId ? starredItems.find((item) => item.entityId === entityId) ?? null : null),
    [starredItems, entityId],
  )

  return { isStarred: !!starredItem, starredItem, isLoading }
}

/**
 * Mutations for starring/unstarring entities.
 */
export const useStarredMutations = () => {
  const { create, delete: deleteEntity } = hooks.useMutations()
  const userId = useLoginStore((s) => s.userId)

  const toggleStar = async (
    entityType: StarrableEntityType,
    entityId: string,
    currentStarredItem: StarredDocument | null,
  ) => {
    if (currentStarredItem) {
      await deleteEntity(currentStarredItem.id)
    } else {
      await create({
        userId: userId ?? '',
        entityType,
        entityId,
        sortOrder: null,
      })
    }
  }

  return { toggleStar, createStar: create, deleteStar: deleteEntity }
}

/** Convenience: starred projects only */
export const useStarredProjects = () => useStarredItems('project')

/** Convenience: starred saved filters only */
export const useStarredSavedFilters = () => useStarredItems('saved_filter')

/** Convenience: starred cycles only */
export const useStarredCycles = () => useStarredItems('cycle')

/** Convenience: starred pages only */
export const useStarredPages = () => useStarredItems('page')

/** Convenience: starred tasks only */
export const useStarredTasks = () => useStarredItems('task')
